import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  Typography,
  CircularProgress,
  Dialog,
  TextField,
  DialogContent,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Description,
  TrendingUp,
  EmojiObjects,
  Clear,
  People,
  Visibility,
} from '@material-ui/icons';

import { DashboardMetricCard } from './DashboardMetricCard';
import PostPreviewModal from './PostPreviewModal';
import ThemeCard from './ThemeCard';
import PostCard from './PostCard';
import { getPostThemes } from '../../../api_handlers/competitor';
import {
  CREATOR_THEME_SORT_COLUMNS,
  sortCreatorThemeRows,
  creatorUsedEntry,
  mergeBrandContentThemes,
  normalizeContentThemesResponse,
} from '../lib/contentThemes';
import { generateContentThemeInsight, generateHookInsight } from '../lib/insights';
import { formatNumber, formatPercentage, getBrandColor } from '../lib/utils';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    borderRadius: theme.shape.borderRadius * 2,
    overflow: 'hidden',
  },
  tableHeader: {
    backgroundColor: theme.palette.background.default,
  },
  // Keep the sort arrow visible (faded) on every sortable header so users know
  // the columns can be sorted, and full-strength on the active column.
  sortLabel: {
    '& .MuiTableSortLabel-icon': { opacity: 0.4, transition: 'opacity 0.2s' },
    '&:hover .MuiTableSortLabel-icon': { opacity: 0.7 },
    '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
      opacity: 1,
      color: theme.palette.primary.main,
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(8),
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(8),
    textAlign: 'center',
  },
  card: {
    borderRadius: theme.shape.borderRadius * 2,
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  highlightBox: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    background: `linear-gradient(to right, rgba(251, 191, 36, 0.1), rgba(234, 179, 8, 0.1), rgba(251, 191, 36, 0.1))`,
    border: `1px solid rgba(251, 191, 36, 0.3)`,
    marginBottom: theme.spacing(3),
  },
  highlightContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  entryList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    // The list used to render every theme at full height, so a 50-theme report
    // pushed the rest of the page far below the fold. Bounded and scrollable.
    maxHeight: 420,
    overflowY: 'auto',
    // Room for the scrollbar so it never sits on top of the engagement badge.
    paddingRight: theme.spacing(0.5),
  },
  cardScroller: {
    maxHeight: 620,
    overflowY: 'auto',
    // Space so the scrollbar never overlaps a card's right-hand stats.
    paddingRight: theme.spacing(1),
    // Hint that there is more below the fold of the scroller.
    maskImage: 'linear-gradient(to bottom, black calc(100% - 16px), transparent 100%)',
    WebkitMaskImage: 'linear-gradient(to bottom, black calc(100% - 16px), transparent 100%)',
  },
  listFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  entryItem: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s',
    cursor: 'pointer',
    backgroundColor: theme.palette.action.hover,
  },
  entryItemWinner: {
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    borderColor: 'rgba(34, 197, 94, 0.3)',
  },
  entryItemSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
  },
  entryMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    minWidth: 0,
    flex: 1,
  },
  brandSplit: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  brandSplitBar: {
    display: 'flex',
    height: 6,
    borderRadius: 3,
    overflow: 'hidden',
    backgroundColor: theme.palette.action.hover,
  },
  brandSplitSegment: {
    height: '100%',
  },
  brandSplitLegend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  brandCounts: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  truncationNote: {
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: 'rgba(251, 191, 36, 0.12)',
    border: '1px solid rgba(251, 191, 36, 0.3)',
    marginBottom: theme.spacing(2),
  },
  brandSplitDot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginRight: 4,
  },
  entryLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: '0.8125rem',
    fontWeight: 500,
  },
  entryMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  engagementBadge: {
    fontSize: '0.75rem',
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 12,
    whiteSpace: 'nowrap',
  },
  engagementBadgeWinner: {
    backgroundColor: '#16a34a',
    color: '#fff',
  },
  engagementBadgeNormal: {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
  },
  insightBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(2),
    marginTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  insightHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  insightText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  exampleStrip: {
    display: 'flex',
    gap: theme.spacing(1.5),
    overflowX: 'auto',
    paddingBottom: theme.spacing(1),
  },
  exampleCard: {
    minWidth: 200,
    maxWidth: 200,
    cursor: 'pointer',
  },
  postCaption: {
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  captionCard: {
    width: 200,
    minHeight: 120,
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  captionCardText: {
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 4,
    WebkitBoxOrient: 'vertical',
  },
  thumbnail: {
    width: 160,
    height: 160,
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
  },
  thumbnailPlaceholder: {
    width: 160,
    height: 160,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  captionSnippet: {
    display: 'block',
    marginTop: 4,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
  },
}));

// Follower tiers, matching the labels used on the Influencers and Posts tabs.
const getTierLabel = (followers) => {
  const count = followers || 0;
  if (count >= 1000000) return 'Mega';
  if (count >= 100000) return 'Macro';
  if (count >= 10000) return 'Micro';
  if (count >= 2000) return 'Nano';
  return 'Normal';
};

const brandColorFor = (brandTitle, competitorBrands) => {
  const index = competitorBrands.findIndex((b) => b.title === brandTitle);
  return getBrandColor(index >= 0 ? index : 0);
};


const entryLabelOf = (explicit, breakdown) => {
  if (explicit?.label) return explicit.label;
  const list = Array.isArray(breakdown) ? breakdown : [];
  const top = list.reduce(
    (best, item) => ((item?.post_count || 0) > (best?.post_count || 0) ? item : best),
    null,
  );
  return top?.label || '—';
};

// Complete results for this browser session, keyed by scopeKey, so a tab
// switch (Detail unmounts this tab) redraws instantly. Only complete results
// are kept; the classifications themselves are stored server-side, so a
// reload or a reopened report is one cheap request, not a re-analysis.
const analysisCache = new Map();

// While the backend is still classifying posts in scope, refetch on this
// interval so the figures fill in without the user doing anything.
const PENDING_POLL_MS = 15000;
const MAX_PENDING_POLLS = 20;

export default function ContentThemesTab({
  selectedBrands = [],
  competitorBrands = [],
  start_date,
  end_date,
  platform = 'instagram',
}) {
  const classes = useStyles();
  const isYouTube = platform === 'yt' || platform === 'youtube';

  const [data, setData] = useState({ themes: [], hooks: [], creators: [], total_posts_analyzed: 0 });
  const [loading, setLoading] = useState(false);
  // A single flag for every failure mode: the endpoint is not deployed yet, so
  // 404s, 500s and network errors all degrade to the same neutral message.
  const [notAvailable, setNotAvailable] = useState(false);

  // Drill-down selection: one theme or one hook at a time.
  const [selected, setSelected] = useState(null); // { kind: 'theme'|'hook', id, label }
  const [postPage, setPostPage] = useState(0);
  // Drill-down filter: which creator's posts to show inside the selected theme.
  const [selectedCreator, setSelectedCreator] = useState(null);
  // Focus a single creator's whole output, independent of the theme drill-down.
  const [creatorFocus, setCreatorFocus] = useState(null);
  // The post being previewed in the modal.
  const [previewPost, setPreviewPost] = useState(null);
  // Free-text filter over whatever posts the modal is currently showing.
  const [postSearch, setPostSearch] = useState('');
  // Guidance is explicit: do not render lists beyond ~50 items. Show the ranked
  // head by default and let the user opt into the tail.
  const [expandedLists, setExpandedLists] = useState({});
  // selectedBrands/competitorBrands are prop arrays with a default of [], so
  // their reference changes on every render. Keying effects off the arrays
  // themselves re-ran the entire analysis continuously; key off their value.
  const scopeKey = [
    [...selectedBrands].sort().join('|'),
    start_date || '',
    end_date || '',
    platform,
  ].join('::');
  // The scope the current data was loaded for, so re-renders never refetch.
  const [analysedScope, setAnalysedScope] = useState(null);
  const pollCount = useRef(0);

  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(0);
  };

  const fetchThemes = useCallback(
    async ({ silent = false } = {}) => {
      if (selectedBrands.length === 0 || competitorBrands.length === 0) return;

      if (!silent) setLoading(true);
      setNotAvailable(false);
      try {
        // One stored rollup per brand; the backend has already classified
        // (or is classifying) every post, so nothing is swept client-side.
        const perBrand = await Promise.all(
          selectedBrands.map(async (title) => {
            const info = competitorBrands.find((b) => b.title === title);
            if (!info) return [title, null];
            const handle = ((isYouTube ? info.yt_handle : info.ig_handle) || '').trim();
            if (!handle || (isYouTube && handle.toLowerCase() === 'test')) return [title, null];
            try {
              const raw = await getPostThemes(handle, {
                startDate: start_date,
                endDate: end_date,
                excludedHandles: isYouTube ? '' : info.excluded_handles,
                youtube: isYouTube,
              });
              return [title, raw && Array.isArray(raw.themes) ? normalizeContentThemesResponse(raw) : null];
            } catch (error) {
              console.error(`Failed to load content themes for ${title}:`, error);
              return [title, null];
            }
          }),
        );

        // Only a total blackout is "no data" — one brand failing should still
        // render the brands that did return.
        if (perBrand.every(([, payload]) => !payload)) {
          setNotAvailable(true);
          setData({ themes: [], hooks: [], creators: [], total_posts_analyzed: 0 });
          return;
        }
        const postsPending = perBrand.reduce(
          (sum, [, payload]) => sum + (payload?.classification?.posts_pending || 0),
          0,
        );
        const result = {
          ...mergeBrandContentThemes(Object.fromEntries(perBrand)),
          posts_pending: postsPending,
        };
        if (postsPending === 0) analysisCache.set(scopeKey, result);
        setData(result);
        setAnalysedScope(scopeKey);
      } catch (error) {
        console.error('Failed to load content themes:', error);
        setNotAvailable(true);
        setData({ themes: [], hooks: [], creators: [], total_posts_analyzed: 0 });
      } finally {
        if (!silent) setLoading(false);
      }
    },
    // scopeKey covers selectedBrands/dates/platform by value; listing them
    // directly rebuilds this callback every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopeKey, competitorBrands],
  );

  // Load whenever the scope has not been loaded yet — it is a stored
  // aggregation now, so there is no reason to make the user ask for it.
  useEffect(() => {
    if (analysedScope !== scopeKey && !loading) fetchThemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysedScope, scopeKey, competitorBrands]);

  // A different report, brand set or date range: show the session copy if
  // there is one, otherwise clear and let the effect above load it.
  useEffect(() => {
    const cached = analysisCache.get(scopeKey);
    pollCount.current = 0;
    setAnalysedScope(cached ? scopeKey : null);
    setData(cached || { themes: [], hooks: [], creators: [], total_posts_analyzed: 0 });
  }, [scopeKey]);

  // Posts still being classified server-side: refetch until they are done.
  useEffect(() => {
    if (!data.posts_pending || analysedScope !== scopeKey || loading) return undefined;
    if (pollCount.current >= MAX_PENDING_POLLS) return undefined;
    const timer = setTimeout(() => {
      pollCount.current += 1;
      fetchThemes({ silent: true });
    }, PENDING_POLL_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, analysedScope, scopeKey, loading]);

  // Reset drill-down and pagination whenever the underlying scope changes.
  useEffect(() => {
    setSelected(null);
    setPage(0);
  }, [scopeKey]);

  // A creator filter only makes sense within one theme.
  useEffect(() => {
    setSelectedCreator(null);
    setPostPage(0);
  }, [selected]);

  useEffect(() => {
    setCreatorFocus(null);
  }, [scopeKey]);

  useEffect(() => {
    setPostSearch('');
  }, [selected, creatorFocus]);

  const { themes, hooks, creators } = data;

  // Resolve a post's creator_id to the full creator record, so the drill-down
  // can name who made each post rather than showing an opaque id.
  const creatorsById = useMemo(() => {
    const index = new Map();
    (creators || []).forEach((creator) => {
      if (creator.creator_id) index.set(String(creator.creator_id), creator);
    });
    return index;
  }, [creators]);

  const themeInsight = useMemo(() => generateContentThemeInsight(themes), [themes]);
  const hookInsight = useMemo(() => generateHookInsight(hooks), [hooks]);

  const leadingTheme = useMemo(
    () =>
      themes.reduce(
        (best, t) => ((t.post_count || 0) > (best?.post_count || 0) ? t : best),
        null,
      ),
    [themes],
  );
  const leadingHook = useMemo(
    () =>
      hooks.reduce((best, h) => ((h.post_count || 0) > (best?.post_count || 0) ? h : best), null),
    [hooks],
  );


  const selectedEntry = useMemo(() => {
    if (!selected) return null;
    const list = selected.kind === 'hook' ? hooks : themes;
    return list.find((e) => e.id === selected.id) || null;
  }, [selected, themes, hooks]);

  // Posts behind the selected entry, and who made them.
  const entryPosts = useMemo(
    () => (selectedEntry && selectedEntry.example_posts) || [],
    [selectedEntry],
  );

  const entryCreators = useMemo(() => {
    const counts = new Map();
    entryPosts.forEach((post) => {
      const key = post.creator_id ? String(post.creator_id) : null;
      if (!key) return;
      const existing = counts.get(key);
      counts.set(key, {
        creator_id: key,
        posts: (existing ? existing.posts : 0) + 1,
        brand: post.brand || (existing ? existing.brand : null),
      });
    });
    return Array.from(counts.values()).sort((a, b) => b.posts - a.posts);
  }, [entryPosts]);

  const visiblePosts = selectedCreator
    ? entryPosts.filter((post) => String(post.creator_id) === selectedCreator)
    : entryPosts;

  // Every post the focused creator made, across all themes. A post belongs to
  // exactly one theme and one hook, so scanning themes yields their full set
  // without double counting.
  const creatorPosts = useMemo(() => {
    if (!creatorFocus) return [];
    const wanted = String(creatorFocus);

    return (themes || []).flatMap((theme) =>
      (theme.example_posts || [])
        .filter((post) => String(post.creator_id) === wanted)
        .map((post) => ({ ...post, theme_label: theme.label })),
    );
  }, [creatorFocus, themes]);

  const creatorName = (id) => {
    const creator = creatorsById.get(String(id));
    return (creator && (creator.name || creator.handle)) || id || 'Unknown creator';
  };


  const filteredCreators = useMemo(() => {
    if (!selected) return creators;
    return creators.filter((c) => creatorUsedEntry(c, selected.id, selected.kind));
  }, [creators, selected]);

  const sortedCreators = useMemo(
    () => sortCreatorThemeRows(filteredCreators, sortField, sortDir),
    [filteredCreators, sortField, sortDir],
  );

  const paginatedCreators = useMemo(
    () => sortedCreators.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedCreators, page, rowsPerPage],
  );

  const handleEntryClick = (entry, kind) => {
    setPage(0);
    setPostPage(0);
    setSelected((prev) =>
      prev && prev.id === entry.id && prev.kind === kind
        ? null
        : { kind, id: entry.id, label: entry.label },
    );
  };

  const openCreatorProfile = (creator) => {
    const handle = creator.handle || creator.creator_id;
    if (!handle) return;
    if (isYouTube) {
      window.open(`https://www.youtube.com/channel/${handle}`, '_blank');
    } else {
      window.open(`https://www.instagram.com/${handle}`, '_blank');
    }
  };

  // Preview in place. Leaving the page threw away the theme/creator context the
  // user had just built up; the modal keeps it and still offers the outbound link.
  const openPost = (post) => setPreviewPost(post);

  if (selectedBrands.length === 0) {
    return (
      <Paper className={classes.tableContainer}>
        <Box className={classes.emptyContainer}>
          <Typography variant="h6" color="textSecondary">
            Please select at least one brand to view content themes
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (loading && creators.length === 0) {
    return (
      <Paper className={classes.tableContainer}>
        <Box className={classes.loadingContainer} style={{ flexDirection: 'column', gap: 12 }}>
          <CircularProgress />
          <Typography variant="body2" color="textSecondary">
            Loading content themes…
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (notAvailable) {
    return (
      <Paper className={classes.tableContainer}>
        <Box className={classes.emptyContainer}>
          <EmojiObjects style={{ fontSize: 32, color: '#c4c8ce' }} />
          <Typography variant="h6" color="textSecondary">
            No posts found to analyse for these brands.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Try widening the date range or selecting a different platform.
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (themes.length === 0 && hooks.length === 0 && creators.length === 0) {
    return (
      <Paper className={classes.tableContainer}>
        <Box className={classes.emptyContainer}>
          <Typography variant="h6" color="textSecondary">
            No content themes found for {selectedBrands.join(', ')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Try widening the date range.
          </Typography>
        </Box>
      </Paper>
    );
  }



  const POST_GRID_PAGE = 24;

  /** Search across creator and caption, the two things a person actually recalls. */
  const matchesPostSearch = (post) => {
    const query = postSearch.trim().toLowerCase();
    if (!query) return true;
    return [post.creator_name, post.creator_handle, post.caption_snippet, creatorName(post.creator_id)]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(query));
  };

  const renderPostGrid = (posts, emptyMessage) => {
    const filtered = posts.filter(matchesPostSearch);
    const shown = filtered.slice(0, (postPage + 1) * POST_GRID_PAGE);

    return (
      <>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Search visible posts by creator or caption..."
          value={postSearch}
          onChange={(event) => setPostSearch(event.target.value)}
          InputProps={{ startAdornment: <Visibility style={{ fontSize: 18, color: '#94a3b8', marginRight: 8 }} /> }}
          style={{ marginBottom: 16 }}
        />

        {filtered.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            {postSearch.trim() ? `No posts match "${postSearch}".` : emptyMessage}
          </Typography>
        ) : (
          <>
            <Grid container spacing={2}>
              {shown.map((post, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={post.id || post.shortcode || post.video_id || index}>
                  <PostCard
                    post={post}
                    platformLabel={isYouTube ? 'YouTube' : 'Instagram'}
                    creatorName={creatorName(post.creator_id)}
                    formatNumber={formatNumber}
                    formatPercentage={formatPercentage}
                    onClick={openPost}
                  />
                </Grid>
              ))}
            </Grid>

            {shown.length < filtered.length && (
              <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                <Button onClick={() => setPostPage(postPage + 1)}>
                  Load more ({filtered.length - shown.length} remaining)
                </Button>
              </Box>
            )}
          </>
        )}
      </>
    );
  };

  const COLLAPSED_CARDS = 10;

  const renderCardList = (entries, kind) => {
    const isExpanded = Boolean(expandedLists[kind]);
    // Highest share first, matching the number printed on each card.
    const ranked = [...entries].sort(
      (a, b) => (b.share_of_posts || 0) - (a.share_of_posts || 0),
    );
    const visible = isExpanded ? ranked : ranked.slice(0, COLLAPSED_CARDS);

    if (ranked.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          Nothing to show yet.
        </Typography>
      );
    }

    return (
      <>
        {/* Bounded in both states. Full-width cards run ~200px each, so even
            the top 10 is ~2,000px — taller than the viewport and pushing the
            creator table off-screen. Scroll the list, not the page. */}
        <Box className={classes.cardScroller}>
          {visible.map((entry) => (
            <ThemeCard
              key={entry.id || entry.label}
              entry={entry}
              brandColor={(brand) => brandColorFor(brand, competitorBrands)}
              onSelect={(selectedItem) => handleEntryClick(selectedItem, kind)}
              formatNumber={formatNumber}
              formatPercentage={formatPercentage}
            />
          ))}
        </Box>

        {ranked.length > COLLAPSED_CARDS && (
          <Box className={classes.listFooter}>
            <Typography variant="caption" color="textSecondary">
              {isExpanded
                ? `Showing all ${ranked.length}`
                : `Showing top ${visible.length} of ${ranked.length}`}
            </Typography>
            <Button
              size="small"
              onClick={() =>
                setExpandedLists((current) => ({ ...current, [kind]: !current[kind] }))
              }
            >
              {isExpanded ? 'Show top 10' : `Show all ${ranked.length}`}
            </Button>
          </Box>
        )}
      </>
    );
  };

  // A proportional bar per brand, so it is obvious at a glance which brand owns
  // a theme rather than only that the theme exists across the report.

  return (
    <Box>
      <Box
        style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}
      >
        <Button size="small" onClick={() => fetchThemes()} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Box>

      {/* Summary metrics */}
      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            icon={Description}
            title="Posts Analysed"
            value={formatNumber(data.total_posts_analyzed || 0)}
            subtitle={`across ${selectedBrands.length} ${
              selectedBrands.length === 1 ? 'brand' : 'brands'
            }`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            icon={EmojiObjects}
            title="Themes Identified"
            value={themes.length}
            subtitle={`${hooks.length} hooks`}
            iconColor="#8b5cf6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            icon={TrendingUp}
            title="Top Theme"
            value={leadingTheme?.label || '—'}
            subtitle={
              leadingTheme ? `${formatPercentage(leadingTheme.avg_engagement_rate || 0)} engagement` : '—'
            }
            iconColor="#16a34a"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            icon={People}
            title="Top Hook"
            value={leadingHook?.label || '—'}
            subtitle={
              leadingHook ? `${formatPercentage(leadingHook.avg_engagement_rate || 0)} engagement` : '—'
            }
            iconColor="#1E9CB2"
          />
        </Grid>
      </Grid>

      {/* Per-brand post counts, and a note while posts are still being classified */}
      {data.posts_by_brand && (
        <Box className={classes.brandCounts}>
          {Object.entries(data.posts_by_brand)
            .sort((a, b) => b[1] - a[1])
            .map(([brand, count]) => (
              <Typography key={brand} variant="caption" color="textSecondary">
                <span
                  className={classes.brandSplitDot}
                  style={{ backgroundColor: brandColorFor(brand, competitorBrands) }}
                />
                {brand}: {formatNumber(count)} posts
              </Typography>
            ))}
        </Box>
      )}

      {data.posts_pending > 0 && (
        <Box className={classes.truncationNote}>
          <Typography variant="caption">
            {formatNumber(data.posts_pending)} more{' '}
            {data.posts_pending === 1 ? 'post is' : 'posts are'} still being classified.
            Figures below cover what is done so far and update automatically.
          </Typography>
        </Box>
      )}

      {/* Leading theme callout */}
      {leadingTheme && (
        <Box className={classes.highlightBox}>
          <Box className={classes.highlightContent}>
            <EmojiObjects style={{ fontSize: 28, color: '#d97706' }} />
            <Box>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                {leadingTheme.label} leads across {selectedBrands.length}{' '}
                {selectedBrands.length === 1 ? 'brand' : 'brands'}
                {leadingTheme.lead_brand ? ` — ${leadingTheme.lead_brand} uses it most` : ''}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formatNumber(leadingTheme.post_count || 0)} posts from{' '}
                {leadingTheme.creators_count || 0} creators ·{' '}
                {formatPercentage(leadingTheme.avg_engagement_rate || 0)} avg engagement
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Themes and hooks rankings */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6" className={classes.sectionTitle}>
                Content Themes
              </Typography>
              {renderCardList(themes, 'theme')}
              {themeInsight && (
                <Box className={classes.insightBox}>
                  <Box className={classes.insightHeader}>
                    <TrendingUp style={{ fontSize: 16, color: '#16a34a' }} />
                    <Typography variant="caption" style={{ fontWeight: 600 }}>
                      Key Insight
                    </Typography>
                  </Box>
                  <Typography className={classes.insightText}>{themeInsight}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6" className={classes.sectionTitle}>
                Hooks
              </Typography>
              {renderCardList(hooks, 'hook')}
              {hookInsight && (
                <Box className={classes.insightBox}>
                  <Box className={classes.insightHeader}>
                    <TrendingUp style={{ fontSize: 16, color: '#16a34a' }} />
                    <Typography variant="caption" style={{ fontWeight: 600 }}>
                      Key Insight
                    </Typography>
                  </Box>
                  <Typography className={classes.insightText}>{hookInsight}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {previewPost && (
        <PostPreviewModal
          post={previewPost}
          isYouTube={isYouTube}
          onClose={() => setPreviewPost(null)}
          creatorName={creatorName(previewPost.creator_id)}
          themeLabel={selectedEntry ? selectedEntry.label : null}
          formatNumber={formatNumber}
          formatPercentage={formatPercentage}
        />
      )}

      {/* Every post by one creator, reached from the Posts count in the
          creator table. Spans themes, so it carries a Theme column. */}
      {creatorFocus && (
        <Dialog open onClose={() => setCreatorFocus(null)} maxWidth="lg" fullWidth scroll="paper">
          <DialogContent>
            <Box className={classes.filterBar}>
              <Box>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  {creatorName(creatorFocus)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatNumber(creatorPosts.length)} posts in this report
                </Typography>
              </Box>
              <Button size="small" startIcon={<Clear />} onClick={() => setCreatorFocus(null)}>
                Close
              </Button>
            </Box>

            {renderPostGrid(creatorPosts, 'No posts available for this creator.')}
          </DialogContent>
        </Dialog>
      )}

      {/* Drill-down: every post behind the selected theme or hook */}
      {selectedEntry && (
        <Dialog open onClose={() => setSelected(null)} maxWidth="lg" fullWidth scroll="paper">
          <DialogContent>
            <Box className={classes.filterBar}>
              <Box>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  {selectedEntry.label}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatNumber(visiblePosts.length)}
                  {selectedCreator
                    ? ` posts by ${creatorName(selectedCreator)}`
                    : ` posts from ${entryCreators.length} creator${entryCreators.length === 1 ? '' : 's'}`}
                </Typography>
              </Box>
              <Button size="small" startIcon={<Clear />} onClick={() => setSelected(null)}>
                Clear
              </Button>
            </Box>

            {/* Who made the posts behind this entry. Click one to see only
                that creator's posts on this theme. */}
            {entryCreators.length > 0 && (
              <Box style={{ marginBottom: 16 }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}
                >
                  Creators posting this {selected.kind}
                </Typography>
                <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {selectedCreator && (
                    <Chip
                      size="small"
                      label="All creators"
                      onClick={() => {
                        setSelectedCreator(null);
                        setPostPage(0);
                      }}
                    />
                  )}
                  {entryCreators.slice(0, 30).map((entry) => {
                    const isActive = selectedCreator === entry.creator_id;
                    return (
                      <Chip
                        key={entry.creator_id}
                        size="small"
                        color={isActive ? 'primary' : 'default'}
                        variant={isActive ? 'default' : 'outlined'}
                        label={`${creatorName(entry.creator_id)} · ${entry.posts}`}
                        onClick={() => {
                          setSelectedCreator(isActive ? null : entry.creator_id);
                          setPostPage(0);
                        }}
                        style={{
                          borderLeft: entry.brand
                            ? `3px solid ${brandColorFor(entry.brand, competitorBrands)}`
                            : undefined,
                          borderRadius: 4,
                        }}
                      />
                    );
                  })}
                </Box>
                {entryCreators.length > 30 && (
                  <Typography variant="caption" color="textSecondary">
                    +{entryCreators.length - 30} more creators
                  </Typography>
                )}
              </Box>
            )}

            {renderPostGrid(
              visiblePosts,
              `No posts available for this ${selected.kind}.`,
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Per-creator breakdown */}
      <Card className={classes.card}>
        <CardContent>
          <Box className={classes.filterBar}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Themes &amp; Hooks by Creator
            </Typography>
            {selected && (
              <>
                <Chip
                  label={`${selected.kind === 'hook' ? 'Hook' : 'Theme'}: ${selected.label}`}
                  size="small"
                  onDelete={() => setSelected(null)}
                  color="primary"
                />
                <Typography variant="body2" color="textSecondary">
                  Showing {filteredCreators.length} of {creators.length} creators
                </Typography>
              </>
            )}
          </Box>

          <TableContainer>
            <Table>
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  {CREATOR_THEME_SORT_COLUMNS.map((col) => (
                    <TableCell
                      key={col.key}
                      align="center"
                      style={{ fontWeight: 600 }}
                      sortDirection={sortField === col.key ? sortDir : false}
                    >
                      <TableSortLabel
                        className={classes.sortLabel}
                        active={sortField === col.key}
                        direction={sortField === col.key ? sortDir : 'asc'}
                        hideSortIcon={false}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCreators.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={CREATOR_THEME_SORT_COLUMNS.length}
                      style={{ textAlign: 'center', padding: 40 }}
                    >
                      <Typography variant="body1" color="textSecondary">
                        No creators used this {selected?.kind || 'theme'}.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCreators.map((creator, index) => (
                    <TableRow key={creator.creator_id || creator.name || index}>
                      <TableCell align="center">
                        <Box
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            cursor: 'pointer',
                            justifyContent: 'center',
                          }}
                          onClick={() => openCreatorProfile(creator)}
                        >
                          <Avatar src={creator.image_url} style={{ width: 40, height: 40 }}>
                            {(creator.name || creator.creator_id || '')
                              .substring(0, 2)
                              .toUpperCase()}
                          </Avatar>
                          <Box style={{ textAlign: 'left' }}>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                              {creator.name || creator.creator_id}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {formatNumber(creator.followers || 0)} followers
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={creator.brand || '—'}
                          size="small"
                          style={{
                            backgroundColor: `${brandColorFor(creator.brand, competitorBrands)}20`,
                            color: brandColorFor(creator.brand, competitorBrands),
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={creator.category || 'Uncategorized'}
                          size="small"
                          style={{ backgroundColor: '#e5e7eb', color: '#374151', fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getTierLabel(creator.followers)}
                          size="small"
                          style={{ backgroundColor: '#e5e7eb', color: '#374151', fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          <span
                            role="button"
                            tabIndex={0}
                            aria-label={`Show the ${creator.post_count || 0} posts by ${
                              creator.name || creator.creator_id
                            }`}
                            title="Show these posts"
                            style={{
                              cursor: 'pointer',
                              textDecoration: 'underline dotted',
                              fontWeight: 600,
                            }}
                            onClick={() => {
                              setCreatorFocus(String(creator.creator_id));
                              setPostPage(0);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setCreatorFocus(String(creator.creator_id));
                                setPostPage(0);
                              }
                            }}
                          >
                            {formatNumber(creator.post_count || 0)}
                          </span>
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={entryLabelOf(creator.top_theme, creator.theme_breakdown)}
                          size="small"
                          style={{ backgroundColor: '#ede9fe', color: '#5b21b6', fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={entryLabelOf(creator.top_hook, creator.hook_breakdown)}
                          size="small"
                          style={{ backgroundColor: '#dbeafe', color: '#1e40af', fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {(creator.theme_breakdown || []).length}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          style={{
                            color: (creator.avg_engagement_rate || 0) > 5 ? '#10b981' : '#374151',
                            fontWeight: (creator.avg_engagement_rate || 0) > 5 ? 600 : 400,
                          }}
                        >
                          {formatPercentage(creator.avg_engagement_rate || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {formatNumber(creator.total_views || 0)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredCreators.length > 0 && (
            <Box className={classes.paginationContainer}>
              <TablePagination
                component="div"
                count={filteredCreators.length}
                page={page}
                onChangePage={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onChangeRowsPerPage={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
